/* ==========================================================================
   JADUZAR INK — project page booking behaviour.

   This file replaces ji-booking-gate.js. The medical questionnaire is no
   longer a lock in front of the cart: a customer can add any service to the
   cart and check out without it, and the questionnaire is asked for once per
   order, after payment, through the order confirmation.

   Two jobs remain, and neither of them ever blocks a purchase.

   1. Medical-service marker
   -------------------------
   When the selected variant is a medical one — decided by the trigger words
   in the section settings, matched case-insensitively against the variant
   title, never by a hardcoded variant id — the panel says so and two
   line-item properties are switched on:

       Medical questionnaire required: Yes
       Medical service:                General Anesthesia

   That is the whole payload. No answer, no health history, no consent value,
   no date of birth. Nothing is written to localStorage, sessionStorage,
   cookies, the URL or analytics.

   2. Payment choice — pay in full, or pay the deposit
   ---------------------------------------------------
   No front-end price illusion. "Pay in full" posts the project variant at
   its own Shopify price. "Pay a deposit" posts a real, separate Shopify
   deposit product at its own Shopify price. Whatever reaches the cart is
   what Shopify charges, and every amount on screen is a Shopify-formatted
   money string — never a number this file converted between currencies.

   The choice is hidden entirely when the deposit product does not exist, and
   hidden again whenever the selected project costs the same as the deposit
   or less, so a deposit can never exceed the total.

   It is also hidden for a medical variant. General Anesthesia has its own
   50/50 payment policy, and the generic €1,000 tattoo deposit must never be
   offered against it.
   ========================================================================== */
(() => {
  'use strict';

  const triggersOf = (value) =>
    String(value || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

  const matches = (variant, triggers) => {
    if (!variant || !triggers.length) return false;
    const haystack = [variant.title]
      .concat(Array.isArray(variant.options) ? variant.options : [])
      .join(' ')
      .toLowerCase();
    return triggers.some((t) => haystack.indexOf(t) !== -1);
  };

  /* A disabled field is not posted. Everything optional on this page starts
     disabled and is only ever switched on deliberately. */
  const carry = (fields, on, value) => {
    fields.forEach((el) => {
      if (!el) return;
      el.disabled = !on;
      if (on && typeof value === 'string') el.value = value;
    });
  };

  /* ----------------------------------------------------------------------
     Bali branch: the real Shopify variant form.
     ---------------------------------------------------------------------- */
  const setupBali = (form) => {
    const marker = form.querySelector('[data-ji-medbar]');
    const pay = form.querySelector('[data-ji-pay]');
    const idField = form.querySelector('[data-ji-variant-id]');
    if (!marker && !pay) return;

    const medicalFields = [
      form.querySelector('[data-ji-prop-experience]'),
      form.querySelector('[data-ji-prop-medical]'),
    ];
    const serviceField = form.querySelector('[data-ji-prop-medical-service]');

    const triggers = marker ? triggersOf(marker.dataset.jiMedbarTriggers) : [];
    const serviceName = marker ? marker.dataset.jiMedbarService || '' : '';

    /* Payment choice state. `projectVariant` is the variant the customer
       actually chose; it is what the form posts in full-payment mode and
       what the deposit line names as its project. */
    const depositVariant = pay ? pay.dataset.jiPayVariant : '';
    const depositCents = pay ? Number(pay.dataset.jiPayCents || 0) : 0;
    const depositMoney = pay ? pay.dataset.jiPayMoney || '' : '';
    const dueOut = pay ? pay.querySelector('[data-ji-pay-due]') : null;
    const noteOut = pay ? pay.querySelector('[data-ji-pay-note]') : null;
    const buttons = pay ? Array.from(pay.querySelectorAll('[data-ji-pay-option]')) : [];
    const payFields = pay
      ? [
          pay.querySelector('[data-ji-prop-payment]'),
          pay.querySelector('[data-ji-prop-project]'),
          pay.querySelector('[data-ji-prop-project-variant]'),
          pay.querySelector('[data-ji-prop-project-total]'),
        ]
      : [];
    const fullNote = noteOut ? noteOut.textContent : '';
    const depositNote = pay ? pay.dataset.jiPayDepositNote || '' : '';

    let option = 'full';
    let projectVariant = idField ? idField.value : '';
    let projectPrice = 0;
    let projectTitle = '';
    let eligible = false;
    let medical = false;

    /* The headline price is already rendered by Liquid and kept in step by
       ji-variants.js in the market's own currency. Reading it back is how
       "due today" stays Markets-native without this file ever formatting an
       amount itself. */
    const currentPriceText = () => {
      const el = form
        .closest('.ji-product__sticky, .ji-product__panel, body')
        .querySelector('[data-ji-price]');
      return el ? el.textContent.trim() : '';
    };

    const paint = () => {
      if (!pay) return;

      pay.hidden = !eligible;
      if (!eligible && option === 'deposit') option = 'full';

      buttons.forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.jiPayOption === option));
        b.classList.toggle('is-on', b.dataset.jiPayOption === option);
      });

      const deposit = eligible && option === 'deposit';

      /* Both amounts through one formatter where there is one, so the
         deposit and the project price cannot disagree about separators. */
      const depositText =
        typeof form.jiMoney === 'function' ? form.jiMoney(depositCents) : depositMoney;

      if (dueOut) dueOut.textContent = deposit ? depositText : currentPriceText();
      if (noteOut) noteOut.textContent = deposit ? depositNote || fullNote : fullNote;

      /* The one line that decides what Shopify charges. */
      if (idField) idField.value = deposit ? depositVariant : projectVariant;

      carry(payFields, deposit);
      if (deposit) {
        const set = (sel, value) => {
          const el = pay.querySelector(sel);
          if (el) el.value = value;
        };
        set('[data-ji-prop-payment]', 'Deposit — balance payable at the location');
        set('[data-ji-prop-project-variant]', projectTitle);
        set('[data-ji-prop-project-total]', currentPriceText());
      }
    };

    const apply = (variant) => {
      medical = matches(variant, triggers);

      if (marker) marker.hidden = !medical;
      carry(medicalFields, medical);
      carry(serviceField ? [serviceField] : [], medical, serviceName);

      if (variant && variant.id) projectVariant = String(variant.id);
      if (variant && typeof variant.price === 'number') projectPrice = variant.price;
      if (variant && variant.title) projectTitle = variant.title;

      /* Three conditions, all of them real: the deposit product exists, the
         project genuinely costs more than the deposit, and this is not a
         medical service — General Anesthesia is paid 50/50 and the generic
         tattoo deposit does not apply to it. */
      eligible = !!(pay && depositCents > 0 && projectPrice > depositCents && !medical);
      paint();
    };

    form.addEventListener('ji:variant', (event) => {
      apply(event.detail ? event.detail.variant : null);
    });

    buttons.forEach((b) => {
      b.addEventListener('click', () => {
        option = b.dataset.jiPayOption === 'deposit' ? 'deposit' : 'full';
        paint();
      });
    });

    /* First paint. ji-variants.js has already announced once by the time this
       runs, and a single-variant product never renders the option markup at
       all, so the server-rendered title is the reliable starting point. */
    apply({
      title: marker ? marker.dataset.jiMedbarInitial || '' : '',
      id: projectVariant,
      price: pay ? Number(pay.dataset.jiPayInitialCents || 0) : 0,
      options: [],
    });
  };

  /* ----------------------------------------------------------------------
     Netherlands branch: the day-session builder.

     One day session already costs exactly the deposit, so the choice only
     appears from two day sessions upwards. Choosing the deposit posts the
     deposit product with a quantity of one and records the planned day
     sessions as a property, so the project is still identifiable on the
     order without any price being misrepresented.
     ---------------------------------------------------------------------- */
  const setupNetherlands = (panel) => {
    const form = panel.querySelector('form');
    const pay = panel.querySelector('[data-ji-pay-nl]');
    if (!form) return;

    const medicalFields = Array.from(form.querySelectorAll('[data-ji-comfort-medical]'));
    const idField = form.querySelector('input[name="id"]');
    const qtyField = form.querySelector('[data-ji-days-input]');

    const projectVariant = pay ? pay.dataset.jiProjectVariant : '';
    const depositVariant = pay ? pay.dataset.jiPayVariant : '';
    const depositCents = pay ? Number(pay.dataset.jiPayCents || 0) : 0;
    const depositMoney = pay ? pay.dataset.jiPayMoney || '' : '';
    const dueOut = pay ? pay.querySelector('[data-ji-pay-due]') : null;
    const noteOut = pay ? pay.querySelector('[data-ji-pay-note]') : null;
    const buttons = pay ? Array.from(pay.querySelectorAll('[data-ji-pay-option]')) : [];
    const payFields = pay
      ? [
          pay.querySelector('[data-ji-prop-payment]'),
          pay.querySelector('[data-ji-prop-project-total]'),
          pay.querySelector('[data-ji-prop-days]'),
        ]
      : [];
    const fullNote = noteOut ? noteOut.textContent : '';
    const depositNote = pay ? pay.dataset.jiPayDepositNote || '' : '';

    let option = 'full';
    let state = { days: 1, comfort: false, sessionsCents: 0, sessionsMoney: '' };

    const paint = () => {
      /* Local Anesthesia is a medical service: requesting it marks the line
         so the confirmation asks for the questionnaire afterwards. */
      carry(medicalFields, state.comfort);

      if (!pay) return;

      const eligible = depositCents > 0 && state.sessionsCents > depositCents;
      pay.hidden = !eligible;
      if (!eligible && option === 'deposit') option = 'full';

      buttons.forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.jiPayOption === option));
        b.classList.toggle('is-on', b.dataset.jiPayOption === option);
      });

      const deposit = eligible && option === 'deposit';

      const depositText =
        typeof state.money === 'function' ? state.money(depositCents) : depositMoney;
      if (dueOut) dueOut.textContent = deposit ? depositText : state.sessionsMoney;
      if (noteOut) noteOut.textContent = deposit ? depositNote || fullNote : fullNote;

      if (idField) idField.value = deposit ? depositVariant : projectVariant;
      /* A deposit is one line at the deposit price, whatever the plan. */
      if (qtyField && deposit) qtyField.value = '1';
      if (qtyField && !deposit) qtyField.value = String(state.days);

      carry(payFields, deposit);
      if (deposit) {
        const set = (sel, value) => {
          const el = pay.querySelector(sel);
          if (el) el.value = value;
        };
        set('[data-ji-prop-payment]', 'Deposit — balance payable at the location');
        set('[data-ji-prop-project-total]', state.sessionsMoney);
        set('[data-ji-prop-days]', String(state.days));
      }
    };

    panel.addEventListener('ji:nl', (event) => {
      if (!event.detail) return;
      state = event.detail;
      paint();
    });

    buttons.forEach((b) => {
      b.addEventListener('click', () => {
        option = b.dataset.jiPayOption === 'deposit' ? 'deposit' : 'full';
        paint();
      });
    });

    paint();
  };

  const init = () => {
    document.querySelectorAll('[data-ji-branch="bali"] form').forEach((form) => {
      if (form.dataset.jiBookingReady === 'true') return;
      form.dataset.jiBookingReady = 'true';
      setupBali(form);
    });
    document.querySelectorAll('[data-ji-nl-panel]').forEach((panel) => {
      if (panel.dataset.jiBookingReady === 'true') return;
      panel.dataset.jiBookingReady = 'true';
      setupNetherlands(panel);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  /* The theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', init);
})();
